import {
  Archive,
  BellRing,
  Building2,
  CalendarClock,
  CalendarDays,
  ClipboardList,
  FileText,
  FlaskConical,
  Gauge,
  Handshake,
  KanbanSquare,
  LayoutDashboard,
  FileCog,
  Lightbulb,
  MapPin,
  MessagesSquare,
  Network,
  Settings,
  Shapes,
  Target,
  Repeat,
  Trash2,
  UserRound,
  Users,
  Weight,
  type LucideIcon,
} from 'lucide-react'

export interface NavItem {
  readonly to: string
  readonly label: string
  readonly icon: LucideIcon
  readonly gestorOnly?: boolean
  /** Com `gestorOnly`: o gerente (responsável de área) também vê — para o próprio time. */
  readonly gerenteToo?: boolean
  readonly keywords?: string
}

export interface NavGroup {
  readonly label: string
  readonly items: readonly NavItem[]
}

export const NAV: readonly NavGroup[] = [
  {
    label: 'Semana',
    items: [
      { to: '/minha-semana', label: 'Minha semana', icon: UserRound, keywords: 'minhas tarefas ações' },
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, keywords: 'painel semana mês ano período kpi áreas relatório' },
      { to: '/quadro', label: 'Quadro de tarefas', icon: KanbanSquare, keywords: 'kanban lista atividades' },
    ],
  },
  {
    // Era "Rotinas" (as reuniões da semana); o nome passou para a tela de rotinas do grupo Pessoas.
    label: 'Reuniões',
    items: [
      { to: '/reuniao-ras', label: 'Reunião RAS', icon: Handshake, keywords: 'ata alinhamento semanal pauta objetivos ações' },
      { to: '/reuniao', label: 'Reunião Q3', icon: MessagesSquare, keywords: 'ata que bom que pena que tal' },
      { to: '/decisoes', label: 'Decisões', icon: Lightbulb, keywords: 'que tal acompanhamento' },
      { to: '/atas', label: 'Lista de ATAS', icon: FileText, keywords: 'histórico pdf' },
    ],
  },
  {
    // Molde do grupo "Time" do SISTEMAS: quem é quem, o peso de cada um e o que se repete.
    label: 'Pessoas',
    items: [
      { to: '/usuarios', label: 'Pessoas', icon: Users, gestorOnly: true, gerenteToo: true, keywords: 'usuários permissões sso acesso papel gestor' },
      // De todos: o banco recorta (o executor vê a própria cadeia e o time).
      { to: '/organograma', label: 'Organograma', icon: Network, keywords: 'hierarquia gestor gerentes executores time árvore pessoas' },
      { to: '/carga', label: 'Carga do time', icon: Weight, gestorOnly: true, gerenteToo: true, keywords: 'peso sobrecarga atrasadas time pessoas' },
      { to: '/rotinas', label: 'Rotinas', icon: Repeat, keywords: 'recorrente checklist diária semanal mensal aderência' },
      { to: '/agenda', label: 'Agenda', icon: CalendarDays, keywords: 'calendário prazo mês semana rotinas reuniões' },
    ],
  },
  {
    label: 'Resultados',
    items: [
      { to: '/desempenho', label: 'Desempenho', icon: Gauge, keywords: 'meta 70 responsável mês' },
      { to: '/gestor', label: 'Visão do gestor', icon: ClipboardList, gestorOnly: true, keywords: 'tendência atraso' },
    ],
  },
  {
    label: 'Administração',
    items: [
      // Configurações: a Lixeira é de quem escreve em área; o resto das seções, só do gestor.
      // O executor não vê nenhuma das duas (canAccessRoute).
      { to: '/configuracoes', label: 'Configurações', icon: Settings, keywords: 'sistema áreas metas lixeira administração' },
    ],
  },
]

/** Seções de `/configuracoes`. Seção que a pessoa não pode ver não aparece. */
export interface SettingsSection {
  readonly slug: string
  readonly label: string
  readonly icon: LucideIcon
  readonly gestorOnly?: boolean
  /** Só o dev admin (ex.: Desenvolvimento). */
  readonly adminOnly?: boolean
  readonly keywords?: string
}

export const SETTINGS_SECTIONS: readonly SettingsSection[] = [
  { slug: 'sistema', label: 'Sistema', icon: Building2, gestorOnly: true, keywords: 'nome time subtítulo gestor dono clonar' },
  { slug: 'areas', label: 'Áreas', icon: Shapes, gestorOnly: true, keywords: 'compras estoque manutenção cor ordem' },
  { slug: 'metas', label: 'Metas', icon: Target, gestorOnly: true, keywords: 'desempenho 70 faixa regular' },
  { slug: 'prazos', label: 'Prazos e avisos', icon: BellRing, gestorOnly: true, keywords: 'vence amanhã atraso notificação sino destaque' },
  { slug: 'reunioes', label: 'Reuniões', icon: CalendarClock, gestorOnly: true, keywords: 'ras q3 dia ideal semana horário atrasada' },
  { slug: 'modelos-ata', label: 'Modelos de ATA', icon: FileCog, gestorOnly: true, keywords: 'empresa rodapé objetivo código formulário revisão pdf' },
  { slug: 'locais-ata', label: 'Locais de ATA', icon: MapPin, gestorOnly: true, keywords: 'sala plataforma teams local' },
  // De todos: cada um vê e restaura as excluídas das áreas em que escreve.
  { slug: 'lixeira', label: 'Lixeira', icon: Trash2, keywords: 'excluídas restaurar' },
  // Só o dev admin: ferramentas de teste (dados fictícios na sessão, SIS-465).
  { slug: 'desenvolvimento', label: 'Desenvolvimento', icon: FlaskConical, adminOnly: true, keywords: 'dados fictícios mock teste popular admin' },
]

export function visibleSettingsSections(isGestor: boolean, isAdmin = false): readonly SettingsSection[] {
  return SETTINGS_SECTIONS.filter((s) => (s.adminOnly ? isAdmin : !s.gestorOnly || isGestor))
}

/** URLs antigas (links salvos) e para onde foram. */
export const LEGACY_REDIRECTS: readonly { readonly from: string; readonly to: string }[] = [
  { from: '/admin', to: '/configuracoes' },
  { from: '/admin/areas', to: '/configuracoes/areas' },
  { from: '/admin/metas', to: '/configuracoes/metas' },
  { from: '/admin/pessoas', to: '/usuarios' },
  { from: '/pessoas', to: '/usuarios' },
  { from: '/lixeira', to: '/configuracoes/lixeira' },
  { from: '/semana', to: '/dashboard' },
  { from: '/calendario', to: '/agenda' },
]

export const ALL_NAV_ITEMS = NAV.flatMap((g) => g.items)

/** Trilha do breadcrumb: a tela e, em Configurações, a seção. */
export function routeTrail(pathname: string): string[] {
  const item = ALL_NAV_ITEMS.find((i) => pathname === i.to || pathname.startsWith(`${i.to}/`))
  if (!item) return ['Central de Tarefas']
  const section = item.to === '/configuracoes' ? SETTINGS_SECTIONS.find((s) => pathname.startsWith(`/configuracoes/${s.slug}`)) : undefined
  return section ? [item.label, section.label] : [item.label]
}

export const ARCHIVE_ICON = Archive
